/**
 * ConnectedOvertime -- wires URL params and Apollo queries to the library's
 * pure OverTimeRenderer component.
 *
 * This follows the same "connected wrapper" pattern as ConnectedTreemap.
 * Existing chart code in src/visualization/charts/overtime continues to work
 * unchanged. New pages or apps can use this wrapper (or compose their own)
 * to adopt the library components.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> year defaults + data availability
 *       -> Apollo query (useQuery)
 *         -> data transformation (useChartDataTransformation)
 *           -> OverTimeRenderer (pure, from @atlas-int/viz)
 */

import React, { useState, useMemo, useCallback, memo } from "react";
import type { ReactElement } from "react";
import { useQuery } from "@apollo/client/react";
import type { DocumentNode } from "graphql";
import { useOvertimeParams } from "../paramSchemas";
import { TradeDirection, LocationDetailLevel, ProductLevel, ProductClass } from "@atlas-int/data/types";
import { getCategoriesForChartLegend } from "@atlas-int/viz";
import { getLocationQualifiers, ColorBy, LegendLabels, TradeDirectionSelectorString, UIView } from "@atlas-int/data";
import { LegendRow } from "@atlas-int/viz/shared";
import { CategoryLabels } from "@atlas-int/viz/shared";
import type { CategoryDatum, TooltipDatum } from "@atlas-int/viz/shared";
import { useVisualizationMetadata } from "../hooks/useVisualizationMetadata";
import { useDataAvailability, useYearDefaults } from "@atlas-int/data";
import { useHiddenCategories } from "@atlas-int/viz/treemap";
import { determineEndpointFacet, convertProductStringToNumberId, allProductsDatum, mapQueryArgumentsToAPIEndpointInputs, getExploreDataArray } from "@atlas-int/data";
import type { ExploreDataRecord, ExploreDataQuery } from "@atlas-int/data";
import { LocationLevel } from "@atlas-int/data";
import type { MetadataFetchState, DeflatorDatum, LocationYearDatum } from "@atlas-int/data/types";
import { useDeflatorsMetadata, useLocationYearMetadata } from "@atlas-int/data";
import { tryValidatedYearRange } from "@atlas-int/data/types";
import {
  useChartDataTransformation,
  ChartWithTooltip,
  YAxisScalingOption,
} from "@atlas-int/viz/overtime";
import type { FindInVizOption } from "@atlas-int/controls";
import VisualizationLayout from "../components/VisualizationLayout";
import { TooltipContent } from "../components/tooltip/TooltipContent";

/**
 * OvertimeTooltipContent -- stable memo'd wrapper so `renderTooltipContent`
 * never creates a fresh closure on every parent render.
 */
const OvertimeTooltipContent = memo(function OvertimeTooltipContent(props: {
  datum: TooltipDatum;
  view: UIView;
  totalValue: number;
  tradeDirection: TradeDirection;
  productClass: ProductClass;
  fillColor: string;
}): ReactElement | null {
  return (
    <TooltipContent
      datum={props.datum}
      view={props.view}
      totalValue={props.totalValue}
      tradeDirection={props.tradeDirection}
      productClass={props.productClass}
      fillColor={props.fillColor}
    />
  );
});

/**
 * OvertimeLegend -- extracted from inline JSX so the legend tree is
 * memoized independently of the main chart.
 */
const OvertimeLegend = memo(function OvertimeLegend({
  legendLabel,
  categoriesForLegend,
  hiddenCategories,
  setHiddenCategories,
  resetText,
}: {
  legendLabel: LegendLabels;
  categoriesForLegend: CategoryDatum[];
  hiddenCategories: string[];
  setHiddenCategories: (categories: string[]) => void;
  resetText: string;
}) {
  return (
    <LegendRow>
      <CategoryLabels
        legendLabel={legendLabel}
        categories={categoriesForLegend}
        allowToggle={true}
        hiddenCategories={hiddenCategories}
        setHiddenCategories={setHiddenCategories}
        resetText={resetText}
        fullWidth={true}
      />
    </LegendRow>
  );
});

/**
 * ConnectedOvertime reads all settings from URL query params, fetches data
 * via Apollo, transforms it, and renders via OverTimeRenderer.
 */
const ConnectedOvertime: React.FC = () => {
  // === URL PARAMS ===
  const [
    {
      tradeFlow,
      importer,
      exporter,
      product: productId,
      view,
      productClass,
      productLevel,
      startYear: yearMin,
      endYear: yearMax,
      servicesClass,
      yAxis,
      tradeDirection,
      locationLevel,
      layout,
      ordering,
    },
    setQuery,
  ] = useOvertimeParams();

  const { getYearRangeForProductClass } = useDataAvailability();

  // Clamp start/end year URL params to the available range for the current product class
  useYearDefaults({
    startYear: yearMin,
    endYear: yearMax,
    productClass,
    setQuery,
    needsYearRange: true,
  });

  // Memoize year range (validated via tryValidatedYearRange)
  const yearRangeForProductClass = useMemo(() => {
    const range = getYearRangeForProductClass(productClass);
    if (!range) return null;
    return tryValidatedYearRange(range.yearMin, range.yearMax);
  }, [productClass, getYearRangeForProductClass]);

  // === STATE ===
  const { hiddenCategories, setHiddenCategories } = useHiddenCategories(
    view,
    ColorBy.Sector,
  );
  const [highlightedItem, setHighlightedItem] = useState<string | undefined>();

  // === COMPUTED VALUES ===
  const tradeDirectionSelector: TradeDirectionSelectorString =
    tradeDirection === TradeDirection.Exports
      ? TradeDirectionSelectorString.Exports
      : TradeDirectionSelectorString.Imports;

  // Stable no-op for setYAxisDomain
  const noOpSetYAxisDomain = useCallback(() => {}, []);

  // === METADATA ===
  const { locationMetadata, productMetadata } =
    useVisualizationMetadata();
  const deflatorsMetadata = useDeflatorsMetadata();

  // Location reference for per-capita data
  const locationReferenceInfo = useMemo(() => {
    let locationReferenceId: string | undefined;
    let locationReferenceLevel: string | undefined;

    const {
      exporterLocationLevel,
      exporterIsUndefined,
      exporterIsWorld,
      importerLocationLevel,
      importerIsUndefined,
      importerIsWorld,
    } = getLocationQualifiers({ exporter, importer });

    if (!exporterIsUndefined && !importerIsUndefined) {
      if (!exporterIsWorld && importerIsWorld) {
        locationReferenceId = exporter;
        locationReferenceLevel = exporterLocationLevel;
      } else if (exporterIsWorld && !importerIsWorld) {
        locationReferenceId = importer;
        locationReferenceLevel = importerLocationLevel;
      } else {
        locationReferenceId = exporter;
        locationReferenceLevel = exporterLocationLevel;
      }
    } else if (exporterIsUndefined) {
      locationReferenceId = importer;
      locationReferenceLevel = importerLocationLevel;
    } else {
      locationReferenceId = exporter;
      locationReferenceLevel = exporterLocationLevel;
    }

    return { locationReferenceId, locationReferenceLevel };
  }, [exporter, importer]);

  const locationYearMetadata = useLocationYearMetadata({
    locationId: locationReferenceInfo.locationReferenceId,
    locationLevel: (locationReferenceInfo.locationReferenceLevel as LocationLevel) || LocationLevel.Country,
  });

  // === QUERY BUILDING ===
  const useProductLevel = useMemo(() => {
    if (view === UIView.Markets) {
      return productId === allProductsDatum.productId
        ? ProductLevel.ProductSection
        : undefined;
    }
    return productLevel;
  }, [view, productId, productLevel]);

  const narrowedInputVariables = useMemo(
    () => mapQueryArgumentsToAPIEndpointInputs({ exporter, importer, view }),
    [exporter, importer, view],
  );

  const queryVariables = useMemo(() => {
    if (!yearRangeForProductClass) return null;
    return {
      productClass,
      yearMin: yearRangeForProductClass.start,
      yearMax: yearRangeForProductClass.end,
      productLevel: useProductLevel,
      servicesClass,
      productId:
        productId === allProductsDatum.productId
          ? undefined
          : convertProductStringToNumberId(productId),
      ...narrowedInputVariables,
    };
  }, [
    productClass,
    yearRangeForProductClass,
    useProductLevel,
    servicesClass,
    productId,
    narrowedInputVariables,
  ]);

  const { queryToUse, queryIsInvalid } = useMemo(
    () => determineEndpointFacet({ view, variables: queryVariables }),
    [view, queryVariables],
  );

  // === DATA FETCHING ===
  const { loading: queryLoading, error, data, previousData } = useQuery(
    queryToUse as DocumentNode,
    {
      variables: queryVariables,
      skip: queryIsInvalid || !yearRangeForProductClass || !queryVariables,
    },
  );
  const inputData = data || previousData;

  // Include metadata loading in overall loading state so the spinner stays
  // visible until the chart has everything it needs to render.
  const activeProductMetadata =
    productClass === ProductClass.HS92Products ? productMetadata.hs92
    : productClass === ProductClass.HS12Products ? productMetadata.hs12
    : productClass === ProductClass.HS22Products ? productMetadata.hs22
    : productMetadata.sitc;
  const metadataLoading =
    locationMetadata.status !== "success" ||
    activeProductMetadata.status !== "success" ||
    deflatorsMetadata.status !== "success" ||
    locationYearMetadata.status !== "success";
  const loading = queryLoading || metadataLoading;

  const narrowedChartInput = useMemo((): { data: ExploreDataRecord[] } => {
    if (!inputData) return { data: [] };
    return {
      data: getExploreDataArray(inputData as ExploreDataQuery) ?? [],
    };
  }, [inputData]);

  // Chart query variables for downstream hooks
  const chartQueryVariables = useMemo(
    () => ({
      view,
      productClass,
      servicesClass,
      productLevel,
      exporter,
      importer,
      yearMin,
      yearMax,
      yAxis,
    }),
    [
      view,
      productClass,
      servicesClass,
      productLevel,
      exporter,
      importer,
      yearMin,
      yearMax,
      yAxis,
    ],
  );

  // === DATA TRANSFORMATION ===
  const { seriesData, totalSum } = useChartDataTransformation({
    inputData: narrowedChartInput,
    queryVariables: chartQueryVariables,
    yearRangeForProductClass:
      yearRangeForProductClass || tryValidatedYearRange(1995, 2024)!,
    hiddenCategories,
    overTimeLayout: layout,
    overTimeOrdering: ordering,
    locationLevel,
    tradeFlow: tradeFlow,
    tradeDirection: tradeDirection,
    tradeDirectionSelector,
    locationMetadata,
    productMetadata,
    locationYearMetadata,
    deflatorsMetadata,
    setYAxisDomain: noOpSetYAxisDomain,
  });

  // === DERIVED VALUES ===
  const totalValue = totalSum;

  const findInVizOptions: FindInVizOption[] = useMemo(() => {
    if (!seriesData) return [];

    if (view === UIView.Markets) {
      return seriesData
        .filter((location) => location.key && location.nameEn)
        .map((location) => ({
          id: location.key,
          label: location.countryIso3Code
            ? `${location.nameEn} (${location.countryIso3Code})`
            : location.nameEn!,
        }));
    } else {
      return seriesData
        .filter((product) => product.key && product.nameEn)
        .map((product) => ({
          id: product.key,
          label: product.productCode
            ? `${product.nameEn} (${product.productCode} ${productClass})`
            : product.nameEn!,
        }));
    }
  }, [seriesData, view, productClass]);

  // === LEGEND ===
  const categoriesForLegend: CategoryDatum[] = getCategoriesForChartLegend({
    view,
    productClass,
  });

  const resetText =
    view === UIView.Markets ? "Show All Regions" : "Show All Sectors";
  const legendLabel =
    view === UIView.Products ? LegendLabels.Sector : LegendLabels.Region;

  // Stable callback for y-axis changes
  const handleYAxisChange = useCallback(
    (yAxis: string) => setQuery({ yAxis: yAxis as YAxisScalingOption }),
    [setQuery],
  );

  // === RENDER ===
  return (
    <VisualizationLayout
      totalValue={totalValue}
      totalValueYear={yearMax}
      findInVizOptions={findInVizOptions}
      highlightedItem={highlightedItem}
      onHighlight={setHighlightedItem}
    >
      <ChartWithTooltip
        inputData={narrowedChartInput}
        error={error}
        loading={loading}
        exporter={exporter}
        importer={importer}
        yearRangeForProductClass={yearRangeForProductClass}
        chartQueryVariables={chartQueryVariables}
        hiddenCategories={hiddenCategories}
        tradeDirection={tradeDirection}
        tradeFlow={tradeFlow}
        locationMetadata={locationMetadata}
        productMetadata={productMetadata}
        locationYearMetadata={locationYearMetadata}
        deflatorsMetadata={deflatorsMetadata}
        highlightedItem={highlightedItem}
        setHighlightedItem={setHighlightedItem}
        view={view}
        totalValue={totalValue}
        productClass={productClass}
        yAxis={yAxis}
        overTimeLayout={layout}
        overTimeOrdering={ordering}
        locationLevel={locationLevel}
        onYAxisChange={handleYAxisChange}
        TooltipContent={OvertimeTooltipContent}
      />
      <OvertimeLegend
        legendLabel={legendLabel}
        categoriesForLegend={categoriesForLegend}
        hiddenCategories={hiddenCategories}
        setHiddenCategories={setHiddenCategories}
        resetText={resetText}
      />
    </VisualizationLayout>
  );
};

export default memo(ConnectedOvertime);
